import { SagaIterator } from "redux-saga";
import { call, SagaGenerator, select } from "typed-redux-saga";
import { IsInterface, isString } from "generic-type-guard";

import { Query, Relationship } from "@com.mgmtp.a12.dataservices/dataservices-access";
import {
	createA12DataServiceDataLoader,
	DataOperation,
	ProvideDataParams
} from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/server-connector";
import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";

import { DocumentReference } from "../../utilities/documentReference";
import { query } from "../../utilities/rpcHelper";
import {
	TEXTBAUSTEINE_KATEGORIE_DOCUMENT,
	TEXTBAUSTEINE_KATEGORIE_READONLY_DOCUMENT,
	TEXTBAUSTEINE_VERFAHREN_DOCUMENT
} from "../../utilities/variables/documentModelName";
import { getFilterByKategorie } from "../../app/slices/textbausteine/selectors";

import {
	getVirtualVerfahrenDocRef,
	isQueryWithSource,
	QueryWithSource,
	RuntimeManifestWithTextbaustein
} from "./textbausteinDataProviderHelpers";
import { createEmptyRoot, createLinkWithVirtualRoot, createRootLinkWithChild } from "./textbausteinLinkCreationHelper";
import { getLatestTextbausteinManifests } from "./textbausteinSelectors";

const { provideData } = createA12DataServiceDataLoader({});

/**
 * Legt fest, welche Abfrage durch das benutzerdefinierte Textbaustein-Verhalten und welche durch das Standardverhalten der tree engine behandelt wird.
 * @param params
 * @returns
 */
export function* handleQueries(params: ProvideDataParams): SagaIterator<DataOperation.QueryResult[]> {
	const { queries, activityId } = params;
	const customQueries: DataOperation.Query[] = [];
	const defaultQueries: DataOperation.Query[] = [];
	for (const query of queries) {
		if (
			DataOperation.Query.ListRootNodes.Query.isAssignableFrom(query) ||
			(isQueryWithSource(query) && query.source.includes(TEXTBAUSTEINE_VERFAHREN_DOCUMENT))
		) {
			customQueries.push(query);
		} else {
			defaultQueries.push(query);
		}
	}

	const queryResults: DataOperation.QueryResult[] = [];

	if (customQueries.length !== 0) {
		const customQueryResults = yield* call(handleCustomQueries, customQueries, activityId);
		customQueryResults.forEach(result => queryResults.push(result));
	}

	if (defaultQueries.length !== 0) {
		// add possible typecheck
		const defaultResults = (yield* call(provideData, {
			...params,
			mutations: [],
			queries: defaultQueries
		})) as DataOperation.ResultSet;
		defaultResults.queryResults.forEach(result => queryResults.push(result));
	}

	return queryResults;
}

function* handleCustomQueries(
	queries: DataOperation.Query[],
	activityId: string
): SagaIterator<DataOperation.QueryResult[]> {
	const queryResults: DataOperation.QueryResult[] = [];

	const latestManifests = yield* select(getLatestTextbausteinManifests);

	for (const query of queries) {
		const queryResult = yield* call(getCustomQueryResult, query, latestManifests);
		if (queryResult) {
			queryResults.push(queryResult);
		}
	}

	const activity = yield* select(ActivitySelectors.activityById(activityId));
	const showAllKategorie = yield* select(getFilterByKategorie(activityId));

	queryResults.forEach(queryResult => {
		if (DataOperation.ListQueryResult.isAssignableFrom(queryResult)) {
			queryResult.entries = queryResult.entries.filter((entry: Relationship.LinkWithDocument) => {
				const entities = entry.linkRef.linkDescriptor.entities;
				const child = entities[0].role === "child" ? entities[0] : entities[1];
				const parent = entities[0].role === "parent" ? entities[0] : entities[1];
				return (
					parent.modelName !== TEXTBAUSTEINE_VERFAHREN_DOCUMENT ||
					child.modelName === TEXTBAUSTEINE_KATEGORIE_READONLY_DOCUMENT
				);
			});
			if (!showAllKategorie && activity?.descriptor.kategorie) {
				queryResult.entries = queryResult.entries.filter((entry: Relationship.LinkWithDocument) => {
					if (isDocumentWithName(entry)) {
						return entry.document.target.root.name === activity.descriptor.kategorie;
					} else {
						return true;
					}
				});
			}
		}
	});

	return queryResults;
}

const isDocumentWithName = new IsInterface()
	.withProperties({
		document: new IsInterface()
			.withProperties({
				target: new IsInterface()
					.withProperties({
						root: new IsInterface()
							.withProperties({
								name: isString
							})
							.get()
					})
					.get()
			})
			.get()
	})
	.get();

function* getCustomQueryResult(
	query: DataOperation.Query,
	latestManifests: RuntimeManifestWithTextbaustein[]
): SagaGenerator<DataOperation.QueryResult | undefined> {
	if (DataOperation.ListQuery.isAssignableFrom(query)) {
		return yield* call(handleCustomListRootNodes, query, latestManifests);
	} else if (isQueryWithSource(query)) {
		const parentDocRef = DocumentReference.fromString(query.source);
		if (parentDocRef.model === TEXTBAUSTEINE_VERFAHREN_DOCUMENT) {
			return yield* call(handleVerfahrenQuery, query, latestManifests, parentDocRef);
		}
	}

	return undefined;
}

/**
 * Erzeugt einen Eintrag für jeden Textbaustein in jedem Manifest. Wenn eine query source geliefert wird, werden nur Einträge für diese Daten erstellt.
 * @param listQuery
 * @param manifests
 * @returns
 */
function* handleCustomListRootNodes(
	listQuery: DataOperation.ListQuery,
	manifests: RuntimeManifestWithTextbaustein[]
): SagaIterator<DataOperation.QueryResult> {
	const targetManifests = listQuery.source
		? manifests.filter(data => getVirtualVerfahrenDocRef(data) === listQuery.source)
		: manifests;

	const entries: Relationship.LinkWithDocument[][] = [];

	for (const manifest of targetManifests) {
		const queryRoot: Query.QueryRoot = {
			projectionName: "document",
			targetDocumentModel: TEXTBAUSTEINE_KATEGORIE_DOCUMENT,
			paging: {
				pageNumber: 0,
				pageSize: 1000
			},
			sort: [
				{
					field: "/root/orderNum",
					direction: Query.Direction.ASC,
					nullHandling: Query.NullHandling.NULLS_FIRST,
					ignoreCase: false
				}
			],
			constraint: {
				operator: "exact_match",
				field: "/root/correlatedFachverfahrenKey",
				value: manifest.identification.key
			}
		};

		const children = yield* call(query, queryRoot);

		if (!children || children.length === 0) {
			if (manifest.textbausteinData?.children) {
				entries.push([createEmptyRoot(manifest)]);
			}
			continue;
		}

		const childEntries: Relationship.LinkWithDocument[] = children.map(child =>
			createRootLinkWithChild(manifest, child, !!listQuery.source)
		);

		entries.push(childEntries);
	}

	return {
		id: listQuery.id,
		entries: entries.reduce((prev, curr) => [...prev, ...curr], [])
	};
}

/**
 * Eine Funktion, die Unterdokumente von virtuellen "Verfahren_D"-Dokumenten abruft und
 * virtuelle Verknüpfungen zwischen dem bestehenden Dokument und dem virtuellen Dokument herstellt.
 * @param queryWithSource
 * @param allManifests
 * @param parentDocRef
 * @returns
 */
function* handleVerfahrenQuery(
	queryWithSource: QueryWithSource,
	allManifests: RuntimeManifestWithTextbaustein[],
	parentDocRef: DocumentReference
): SagaIterator<DataOperation.QueryResult | undefined> {
	const fvKey = allManifests.find(data => getVirtualVerfahrenDocRef(data) === queryWithSource.source)?.identification
		?.key;
	if (!fvKey) {
		return undefined;
	}

	const queryRoot: Query.QueryRoot = {
		projectionName: "document",
		targetDocumentModel: TEXTBAUSTEINE_KATEGORIE_DOCUMENT,
		paging: {
			pageNumber: 0,
			pageSize: 1000
		},
		constraint: {
			operator: "exact_match",
			field: "/root/correlatedFachverfahrenKey",
			value: fvKey
		}
	};

	const children = yield* call(query, queryRoot);
	if (!children) {
		return undefined;
	}

	const entries =
		children.map(child =>
			createLinkWithVirtualRoot({ docRef: queryWithSource.source, model: parentDocRef.model }, child.document)
		) ?? [];

	return {
		id: queryWithSource.id,
		entries
	};
}
